/**
 * Shared person photo cache — keyed by personId.
 *
 * Both useOperationalVisitors (visitors list) and useGlobalEventStream (events
 * sidebar) load photos for the same people. This module-level cache prevents
 * duplicate downloads: whichever component resolves the photo first writes it
 * here, and subsequent requests return the cached value immediately.
 *
 * Rules:
 * - null means "we already tried and found no photo" (don't retry)
 * - undefined / missing key means "not fetched yet"
 * - string means a resolved absolute URL (or data URL)
 */

import { fetchWithAuth, makeAbsolutePhotoUrl } from '../services/api';
import { logger } from '../services/logger';

/** Resolved photo URLs (or null = confirmed no photo) keyed by personId */
export const personPhotoCache: Record<string, string | null> = {};

/** In-flight fetch Promises keyed by personId — prevents duplicate parallel requests */
export const personPhotoPending: Record<string, Promise<string | null>> = {};

/** Store a resolved photo URL (or null) for a person */
export function setPersonPhoto(personId: string, url: string | null): void {
  personPhotoCache[personId] = url;
}

/** Read the cached photo URL for a person (undefined = not yet cached) */
export function getPersonPhoto(personId: string): string | null | undefined {
  return personId in personPhotoCache ? personPhotoCache[personId] : undefined;
}

/** Returns true if we already have a cached result (including null) */
export function hasPersonPhoto(personId: string): boolean {
  return personId in personPhotoCache;
}

/** Clear the entire cache (e.g. on manual refresh) */
export function clearPersonPhotoCache(): void {
  for (const key in personPhotoCache) {
    delete personPhotoCache[key];
  }
  for (const key in personPhotoPending) {
    delete personPhotoPending[key];
  }
}

/**
 * Fetch and cache a person's photo, deduplicating concurrent requests for the
 * same personId. Shared by the events panel and the real-time toast so an
 * RFID-badge access shows the person's photo the same way a facial-recognition
 * access does, instead of relying on the backend to include it inline.
 */
export async function fetchPersonPhoto(personId: string): Promise<string | null> {
  if (personId in personPhotoCache) {
    return personPhotoCache[personId];
  }
  if (personId in personPhotoPending) {
    return personPhotoPending[personId];
  }

  const fetchPromise = (async () => {
    try {
      const response = await fetchWithAuth(`/api/v1/people/${personId}/photos`);
      if (!response.ok) {
        setPersonPhoto(personId, null);
        return null;
      }

      const photos = await response.json();
      if (Array.isArray(photos) && photos.length > 0) {
        const primaryPhoto = photos.find((p: any) => p.is_primary === true);
        const facialPhoto = photos.find((p: any) => p.photo_type === 'facial_recognition');
        const selectedPhoto = primaryPhoto || facialPhoto || photos[0];

        if (selectedPhoto?.photo_url) {
          const photoUrl = makeAbsolutePhotoUrl(selectedPhoto.photo_url);
          setPersonPhoto(personId, photoUrl || null);
          return photoUrl || null;
        }
      }

      setPersonPhoto(personId, null);
      return null;
    } catch (error) {
      logger.error('personPhotoCache', 'Failed to fetch person photo', error);
      setPersonPhoto(personId, null);
      return null;
    } finally {
      delete personPhotoPending[personId];
    }
  })();

  personPhotoPending[personId] = fetchPromise;
  return fetchPromise;
}

/**
 * Resolve a person's photo for immediate rendering (e.g. a toast). A warm cache
 * returns synchronously-fast; a cold cache waits up to `timeoutMs` for the fetch
 * before giving up, so a first-ever event for someone doesn't hang the toast.
 */
export async function resolvePersonPhoto(personId: string, timeoutMs = 600): Promise<string | undefined> {
  const cached = getPersonPhoto(personId);
  if (cached !== undefined) return cached || undefined;

  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs));
  const result = await Promise.race([fetchPersonPhoto(personId), timeout]);
  return result || undefined;
}
